import ComponentManager, {ComponentOptions, ComponentOptionsInterface} from "../../componentFramework";
import Log from "../../scripts/Log";
import EventListenerManager from "../EventListenerManager";
import EventManager from "../EventManager";
import Button from "../Button";
import JsfAjax from "../../scripts/jsf/JsfAjax";

export interface DialogEventMap {
	"stepChange": {currentStep: string | undefined};
}

const defaultOptions = {
	submitOnEnter: false
};
export type DefaultOptionsType = typeof defaultOptions;

/**
 * This class represents the visual state of the dialog. Actual functionality is
 * done in Java code.
 * To cancel or submit this form, that is typically done through a JSF action or Java code.
 * But there is also submit and cancel methods on this class which will cause the JSF actions to be called.
 */
@ComponentManager.register()
export default class Dialog<EventMap extends DialogEventMap = DialogEventMap, InstanceOptions extends ComponentOptionsInterface = ComponentOptionsInterface> {
	public static componentStyleClass = "dialog";
	private static readonly visibleStyleClass = "dialog--isVisible";
	private static readonly autoFocusStyleClass = "dialog--autoFocus";
	private static nextDialogZIndex = NaN;

	private readonly dialogElementHadTabIndex: boolean;

	protected readonly options: ComponentOptions<DefaultOptionsType & InstanceOptions>;
	protected readonly eventListenerManager = new EventListenerManager();

	private readonly mutationObserver: MutationObserver;
	private stepAtLastCheck: string | undefined;
	private shouldRefocus: string | null = null;

	private readonly eventManager = new EventManager<EventMap>();
	public readonly addEventListener = this.eventManager.addEventListener;
	public readonly removeEventListener = this.eventManager.removeEventListener;

	constructor(protected readonly rootElement: HTMLElement, instanceOptions?: InstanceOptions) {
		this.options = new ComponentOptions(this, rootElement, defaultOptions).extend(instanceOptions);

		if (isNaN(Dialog.nextDialogZIndex)) {
			Dialog.nextDialogZIndex = parseInt(getComputedStyle(rootElement).zIndex);
		}
		rootElement.style.zIndex = Dialog.nextDialogZIndex.toString();
		rootElement.parentElement!.style.zIndex = Dialog.nextDialogZIndex.toString();
		Dialog.nextDialogZIndex++;
		if (rootElement.hasAttribute("tabIndex")) {
			this.dialogElementHadTabIndex = true;
		} else {
			this.dialogElementHadTabIndex = false;
			rootElement.tabIndex = -1;
		}

		this.eventListenerManager.manage(rootElement).addEventListener("keydown", event => {
			if (event.key === "Escape") {
				this.cancel();
				event.stopImmediatePropagation();
				event.preventDefault();
			} else if (event.key === "Enter") {
				if (this.options.getValues().submitOnEnter) {
					this.submit();
					this.shouldRefocus = (event.target as HTMLElement).id;
				}
				event.stopImmediatePropagation();
				event.preventDefault();
			}
		});
		this.eventListenerManager.manage(rootElement).addEventListener("click", () => {
			if (document.activeElement === null || document.activeElement === document.body) {
				Dialog.updateFocus();
			}
		});
		this.eventListenerManager.manage(JsfAjax.getInstance(rootElement.ownerDocument.defaultView!)).addEventListener("always", () => {
			if (Dialog.getTopMostVisibleDialog() === rootElement && (document.activeElement === null || document.activeElement === document.body)) {
				Dialog.updateFocus();
			}
		});

		this.addEventListener("stepChange", () => {
			console.log(document.activeElement);
			if (Dialog.getTopMostVisibleDialog() === rootElement && (document.activeElement === null || document.activeElement === document.body)) {
				this.refocusOnAutoFocusElement();
			}
		});
		this.refocusOnAutoFocusElement();

		this.mutationObserver = new MutationObserver(this.onMutation);
		this.mutationObserver.observe(rootElement, {
			childList: true,
			subtree: true,
			characterData: false,
			attributes: false
		});
		this.onMutation(); // run once
	}

	@ComponentManager.on("removed")
	protected componentRemoved() {
		this.eventManager.destroy();
		this.mutationObserver.disconnect();
		this.eventListenerManager.removeListeners();
		if (!this.dialogElementHadTabIndex) {
			this.getRootElement().removeAttribute("tabIndex");
		}

		Dialog.updateFocus();
	}

	private onMutation = () => {
		const currentStep = this.getCurrentStep();
		if (currentStep !== this.stepAtLastCheck) {
			this.stepAtLastCheck = currentStep;
			this.eventManager.notifyEventListeners("stepChange", {currentStep});
		} else if (this.shouldRefocus !== null) {
			this.refocusOnAutoFocusElement();
		}
	}

	protected getRootElement(): HTMLElement {
		return this.rootElement;
	}

	protected getCurrentStep() {
		return this.getRootElement().querySelector("form")?.getAttribute("data-dialogCurrentStep") ?? undefined;
	}

	private static updateFocus() {
		const topMostVisibleDialog = Dialog.getTopMostVisibleDialog();
		if (topMostVisibleDialog !== null) {
			topMostVisibleDialog.focus({preventScroll: true});
		}
		return topMostVisibleDialog;
	}

	public static getTopMostVisibleDialog() {
		const elements = document.querySelectorAll<HTMLElement>(".dialog");
		let highestElement = null;
		for (let i = 0; i < elements.length; i++) {
			let element = elements[i]!;
			if (highestElement === null || parseInt(highestElement.style.zIndex) < parseInt(element.style.zIndex)) {
				highestElement = element;
			}
		}
		return highestElement;
	}

	public cancel() {
		const cancelButtonStyleClass = ".button--cancel";
		const buttonElem = this.getRootElement().querySelector<HTMLElement>(cancelButtonStyleClass);
		if (buttonElem !== null) {
			(ComponentManager.getComponent(buttonElem, Button) ?? buttonElem).click();
		} else {
			Log.warn(`Your dialog does not contain a HTMLElement with a "${cancelButtonStyleClass}" style class.`)
		}
	}

	public submit() {
		const primaryButtonStyleClass = ".button--primary";
		const buttonElem = this.getRootElement().querySelector<HTMLElement>(primaryButtonStyleClass);
		if (buttonElem !== null) {
			(ComponentManager.getComponent(buttonElem, Button) ?? buttonElem).click();
		} else {
			Log.warn(`Your dialog does not contain a HTMLElement with a "${primaryButtonStyleClass}" style class.`)
		}
	}

	public setVisibility(visible: boolean) {
		this.getRootElement().classList.toggle(Dialog.visibleStyleClass, visible);
	}

	private refocusOnAutoFocusElement = () => {
		const resultOfOnCreationUpdateFocus = Dialog.updateFocus();
		if (resultOfOnCreationUpdateFocus === this.rootElement) {
			const autoFocusElement = this.shouldRefocus !== null ? document.getElementById(this.shouldRefocus)
				: this.rootElement.querySelector('.' + Dialog.autoFocusStyleClass);
			this.shouldRefocus = null;
			if (autoFocusElement instanceof HTMLElement) {
				autoFocusElement.focus({preventScroll: true});
			}
		}
	};
}
